// src/features/inventory/components/ReceiptCostFields.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, FormControlLabel, InputAdornment, TextField, Typography } from '@mui/material';
import { valuationAPI } from '../services/valuation';
import { useApi } from '../../../hooks/api/useApi';
import { useInventoryCostPermissions } from '../../auth/hooks/usePermissions';
import { formatAmountInput, formatDKK } from '../../../utils/formatters/currencyFormatter';
import InfoTip from '../../../components/common/InfoTip';

// The API stores costs as DECIMAL(12, 4)
const MAX_UNIT_COST = 99999999.9999;
// "1234,5" / "1234.5", up to 4 decimals
const PLAIN_AMOUNT = /^\d+(?:[.,]\d{1,4})?$/;
// Danish thousands grouping: "1.234,50", "12.345.678"
const GROUPED_AMOUNT = /^\d{1,3}(?:\.\d{3})+(?:,\d{1,4})?$/;

/**
 * Parse a cost typed with either decimal separator, or Danish thousands
 * grouping. Returns null when empty or not something the API accepts (more
 * than 4 decimals, exponents, negative or too large), so the dialog never
 * submits a value that would come back as a 422.
 */
export const parseUnitCost = (value) => {
  if (value === '' || value == null) return null;
  const text = String(value).replace(/[\s\u00a0]/g, '');
  let normalized;
  if (PLAIN_AMOUNT.test(text)) normalized = text.replace(',', '.');
  else if (GROUPED_AMOUNT.test(text)) normalized = text.replace(/\./g, '').replace(',', '.');
  else return null;
  const parsed = Number(normalized);
  return parsed > MAX_UNIT_COST ? null : parsed;
};

/**
 * Optional cost per unit on a receipt.
 *
 * Collapsed by default to one line naming the price the receipt will be
 * valued at (the item's cost price); most receipts cost the same as the last
 * one, so nobody should have to type it. "Angiv kostpris" opens the field
 * for the odd receipt at another price. An empty value means "use the item's
 * price" and is sent as no unit_cost.
 *
 * Renders nothing without inventory:cost:update, so staff who only book
 * stock never see prices; their receipts are valued at the item's price.
 */
export default function ReceiptCostFields({ open, itemId, uom, value, onChange, updatePrice, onUpdatePriceChange, disabled }) {
  const { t } = useTranslation(['inventory']);
  const { canRead, canUpdate } = useInventoryCostPermissions();
  const { execute } = useApi();
  const [currentPrice, setCurrentPrice] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!open) return;
    setExpanded(false);
    setCurrentPrice(null);
    if (!canRead || !canUpdate || !itemId) return;
    let cancelled = false;
    execute(() => valuationAPI.getItem(itemId), { showError: false }).then((result) => {
      if (!cancelled && result.success) setCurrentPrice(result.data.data?.cost_price ?? null);
    });
    return () => { cancelled = true; };
  }, [open, canRead, canUpdate, itemId, execute]);

  if (!canUpdate) return null;

  const expand = () => {
    setExpanded(true);
    // Start from the item's price: a receipt at another price is usually close to it
    if (value === '' && currentPrice != null) onChange(formatAmountInput(currentPrice));
  };

  const collapse = () => {
    setExpanded(false);
    onChange('');
  };

  if (!expanded) {
    let summary;
    if (!canRead) summary = t('inventory:items.receipt_cost.summary_item_price');
    else if (currentPrice == null) summary = t('inventory:items.receipt_cost.summary_unknown');
    else summary = t('inventory:items.receipt_cost.summary', { price: formatDKK(currentPrice), uom });

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">{summary}</Typography>
          <InfoTip title={t('inventory:help.value.receipt_cost')} />
        </Box>
        <Button size="small" onClick={expand} disabled={disabled} sx={{ flexShrink: 0 }}>
          {t('inventory:items.receipt_cost.enter')}
        </Button>
      </Box>
    );
  }

  const parsed = parseUnitCost(value);
  const changesPrice = parsed != null && parsed !== currentPrice;

  return (
    <Box>
      <TextField
        label={t('inventory:items.receipt_cost.label', { uom })}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoFocus
        fullWidth
        error={value !== '' && parsed == null}
        helperText={value !== '' && parsed == null
          ? t('inventory:items.receipt_cost.invalid')
          : t('inventory:items.receipt_cost.helper')}
        slotProps={{
          input: { endAdornment: <InputAdornment position="end">{t('inventory:items.currency_suffix')}</InputAdornment> },
          htmlInput: { inputMode: 'decimal' },
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        {changesPrice ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={updatePrice}
                onChange={(e) => onUpdatePriceChange(e.target.checked)}
                disabled={disabled}
                size="small"
              />
            }
            label={(
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                {t('inventory:items.receipt_cost.update_price')}
                <InfoTip title={t('inventory:help.value.receipt_update_price')} />
              </Box>
            )}
            slotProps={{ typography: { variant: 'body2' } }}
          />
        ) : <span />}
        <Button size="small" onClick={collapse} disabled={disabled} sx={{ flexShrink: 0 }}>
          {t('inventory:items.receipt_cost.use_item_price')}
        </Button>
      </Box>
    </Box>
  );
}
